import c from 'classnames';
import { ComponentProps, useEffect, useState } from 'react';
import {
  ToastContainer,
  ToastOptions,
  TypeOptions,
  toast,
} from 'react-toastify';
import { Button } from '../Button';
import { Typography } from '../Typography';
import styles from './THToast.module.css';
import ToastIcon from './ToastIcon';

export type Action = {
  label: string;
  onClick: VoidFunction;
  iconName?: string;
};

interface THToastProps {
  type?: TypeOptions;
  message: string;
  action?: Action;
  closeToast?: VoidFunction;
}

interface THToastOptions extends ToastOptions {
  action?: Action;
}

export function THToastContainer(props: ComponentProps<typeof ToastContainer>) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  return (
    <ToastContainer
      {...props}
      position='bottom-center'
      hideProgressBar
      closeButton={false}
      icon={false}
      toastStyle={{
        backgroundColor: 'transparent',
        boxShadow: 'unset',
        margin: 0,
        padding: 0,
        overflow: 'visible',
      }}
      style={{ width: '100vw', maxWidth: '40rem' }}
    />
  );
}

export function THToast({ type, message, action, closeToast }: THToastProps) {
  return (
    <div className={c(styles.container, styles[type ?? 'success'])}>
      <ToastIcon type={type ?? 'success'} />
      <Typography variant='heading-m'>{message}</Typography>
      <div className={styles.buttonsContainer}>
        {action && (
          <Button
            variant='text'
            label={action.label}
            iconNameRight={action.iconName}
            className={styles.actionButton}
            onClick={action.onClick}
            color='darkPurple'
          />
        )}
        <Button
          iconNameLeft={'closeSm'}
          className={styles.closeButton}
          variant='text'
          onClick={closeToast}
        />
      </div>
    </div>
  );
}

const createToastMethod = (
  toastFn: (typeof toast)['success' | 'error' | 'warning' | 'info'],
  type: TypeOptions,
) => {
  return (message: string, options?: THToastOptions) => {
    const { action, ...toastOptions } = options || {};
    return toastFn(
      toastProps => (
        <THToast
          message={message}
          type={type}
          action={action}
          closeToast={toastProps.closeToast}
        />
      ),
      { containerId: 'new-container', ...toastOptions },
    );
  };
};

const thToast = (message: string, options?: THToastOptions) => {
  const { action, ...toastOptions } = options || {};
  return toast(
    toastProps => (
      <THToast
        message={message}
        action={action}
        closeToast={toastProps.closeToast}
      />
    ),
    { containerId: 'new-container', ...toastOptions },
  );
};

thToast.success = createToastMethod(toast.success, 'success');
thToast.error = createToastMethod(toast.error, 'error');
thToast.info = createToastMethod(toast.info, 'info');
thToast.warning = createToastMethod(toast.warning, 'warning');

export { thToast };
